import { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';
import { I18n } from '../components';
import { useDispatch, useSelector } from 'react-redux';
import { COLOR, FONT, THEME } from '../../constants/styles';

import { getAvailableFeaturesForStaff } from '../../selectors/employers';
import { getAdminSchedulerEventTypes } from '../../selectors/scheduler';
import { appointmentLabelForType, iconForType } from '../airforce/scheduler/common_views';
import { setSelectedEventType } from '../airforce/scheduler/slice';
const PHA_ICON = require('../../../resources/icon-pha.png');
const SCHEDULING_ICON = require('../../../resources/nav/icon-scheduling.png');
const SCHEDULING_ICON_EXPANDED = require('../../../resources/nav/icon-scheduling-blank-expanded.png');
const CALENDAR_ICON = require('../../../resources/nav/icon-calendar.png');
const REPORTS_ICON = require('../../../resources/nav/icon-reports.png');
const SUMMARY_ICON = require('../../../resources/nav/icon-squad-summary.png');
const PATIENTS_ICON = require('../../../resources/nav/icon-patients.png');
const STAFF_ICON = require('../../../resources/nav/icon-staff.png');
const MESSAGES_ICON = require('../../../resources/nav/icon-messages.png');
const CONSULTS_ICON = require('../../../resources/nav/icon-consults.png');
const ON_CALL_ICON = require('../../../resources/nav/icon-on-call.png');
const BASE_SETUP_ICON = require('../../../resources/nav/icon-platform-setup.png');

// import { HOME_TAB, CALENDAR_TAB, MESSAGING_TAB, SETUP_TAB } from '../../constants/tabs';
import { EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY } from '../../constants/eventTypes';

import { HOME_TAB, CALENDAR_TAB, MESSAGING_TAB, SETUP_TAB } from '../../constants/tabs';

import { MainNavItem } from './sidebar_nav_item';

const isCMREventType = (eventType) => {
  return [EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY].includes(eventType);
};

export default memo(function SideNav(props) {
  const dispatch = useDispatch();

  const availableEventTypes = useSelector(getAdminSchedulerEventTypes);
  const selectedEventType = useSelector((state) => state.screens.scheduler.selectedEventType);
  const availableFeatures = useSelector(getAvailableFeaturesForStaff);

  const openScreen = useCallback(
    (screenConfig) => {
      const { screen, passProps } = screenConfig;
      if (screen === 'Scheduling' && passProps?.eventType) {
        dispatch(setSelectedEventType(passProps.eventType));
      }
      props.openScreen?.(screenConfig);
    },
    [props.openScreen]
  );

  const navItems = useMemo(() => {
    const items = [];

    const firstEventType = availableEventTypes?.[0];
    if (firstEventType) {
      items.push({
        label: 'Scheduling',
        icon: SCHEDULING_ICON,
        iconExpanded: SCHEDULING_ICON_EXPANDED,
        screenConfig: {
          screen: 'Scheduling',
          passProps: { eventType: firstEventType },
          nativeTab: HOME_TAB,
        },
        defaultExpanded: false,
        selected: props.currentTab === 'Scheduling',
        subItems: availableEventTypes
          .filter((type) => type !== EVENT_TYPE_CMR_FLY)
          .map((eventType) => ({
            label: appointmentLabelForType(eventType),
            icon: iconForType(eventType),
            screenConfig: {
              screen: 'Scheduling',
              passProps: { eventType },
              nativeTab: HOME_TAB,
            },
            selected:
              props.currentTab === 'Scheduling' &&
              (eventType === selectedEventType ||
                (isCMREventType(eventType) && isCMREventType(selectedEventType))),
          })),
      });
    }

    if (availableFeatures.geniusPha) {
      items.push({
        label: 'PHA',
        icon: PHA_ICON,
        screenConfig: { screen: 'PHA', nativeTab: HOME_TAB },
        selected: props.currentTab === 'PHA',
      });
    }

    if (availableFeatures.summary) {
      items.push({
        label: 'Unit Summary',
        icon: SUMMARY_ICON,
        screenConfig: { screen: 'Summary', nativeTab: HOME_TAB },
        selected: props.currentTab === 'Summary',
      });
    }
    if (availableFeatures.patients) {
      items.push({
        label: I18n.c1_aec('employer.residents'),
        icon: PATIENTS_ICON,
        screenConfig: { screen: 'Patients', nativeTab: HOME_TAB },
        selected: props.currentTab === 'Patients',
      });
    }
    if (availableFeatures.staff) {
      items.push({
        label: 'Staff',
        icon: STAFF_ICON,
        screenConfig: { screen: 'Staff', nativeTab: HOME_TAB },
        selected: props.currentTab === 'Staff',
      });
    }
    if (availableFeatures.reports) {
      items.push({
        label: 'Reports',
        icon: REPORTS_ICON,
        screenConfig: { screen: 'Reports', nativeTab: HOME_TAB },
        selected: props.currentTab === 'Reports',
      });
    }
    if (availableFeatures.referrals) {
      items.push({
        label: 'Consults',
        icon: CONSULTS_ICON,
        screenConfig: { screen: 'Consults', nativeTab: HOME_TAB },
        selected: props.currentTab === 'Consults',
      });
    }
    if (availableFeatures.oncall) {
      items.push({
        label: 'On Call',
        icon: ON_CALL_ICON,
        screenConfig: { screen: 'OnCall', nativeTab: HOME_TAB },
        selected: props.currentTab === 'OnCall',
      });
    }
    items.push({
      label: 'Calendar',
      icon: CALENDAR_ICON,
      screenConfig: { screen: 'Calendar', nativeTab: CALENDAR_TAB },
      selected: props.currentTab === 'Calendar',
    });
    items.push({
      label: 'Messages',
      icon: MESSAGES_ICON,
      screenConfig: { screen: 'Messages', nativeTab: MESSAGING_TAB },
      selected: props.currentTab === 'Messages',
    });
    items.push({
      label: 'Base Setup',
      icon: BASE_SETUP_ICON,
      screenConfig: { screen: 'Setup', nativeTab: SETUP_TAB },
      selected: props.currentTab === 'Setup',
    });
    return items;
  }, [availableEventTypes, availableFeatures, selectedEventType, props.currentTab]);

  const firstAvailableScreen = navItems?.[0]?.screenConfig;
  const secondAvailableScreen = navItems?.[1]?.screenConfig;

  useEffect(() => {
    if (availableFeatures.geniusPha) {
      openScreen(secondAvailableScreen);
    } else if (firstAvailableScreen) {
      openScreen(firstAvailableScreen);
    }
  }, [firstAvailableScreen?.screen, secondAvailableScreen?.screen, openScreen]);

  return (
    <>
      <View style={styles.mainNav}>
        {navItems.map((item) => (
          <MainNavItem key={item.label} {...item} onPress={openScreen} />
        ))}
      </View>
    </>
  );
});

const styles = StyleSheet.create({
  mainNav: {
    // backgroundColor: COLOR.labelText,
  },
});
